import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { DataTable } from '@/components/app/DataTable';
import { ErrorState } from '@/components/app/ErrorState';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAlertPolicies, useUpdateAlertPolicy } from '../hooks';
import { SeverityBadge } from '../components/SeverityBadge';
import { humanizeError } from '@/lib/apiClient';
import type { AlertSeverity, NotificationAlertPolicy } from '../types';

export function AlertsPoliciesPage() {
  const { data, isLoading, isError, error, refetch } = useAlertPolicies();
  const [editing, setEditing] = React.useState<NotificationAlertPolicy | null>(null);

  const columns = React.useMemo<ColumnDef<NotificationAlertPolicy>[]>(
    () => [
      {
        accessorKey: 'displayName',
        header: 'Policy',
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.displayName}</div>
            <div className="font-mono text-xs text-muted-foreground">{row.original.policyKey}</div>
          </div>
        ),
      },
      {
        accessorKey: 'severity',
        header: 'Severity',
        cell: ({ row }) => <SeverityBadge severity={row.original.severity} />,
      },
      {
        accessorKey: 'isActive',
        header: 'Active',
        cell: ({ row }) =>
          row.original.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="muted">Paused</Badge>,
      },
      {
        id: 'threshold',
        header: 'Rule',
        cell: ({ row }) => (
          <code className="text-xs">
            {row.original.query.ref} {row.original.query.comparison} {row.original.query.threshold}
          </code>
        ),
      },
      {
        id: 'cooldown',
        header: 'Cooldown',
        cell: ({ row }) => <span className="tabular-nums">{row.original.cooldownMinutes}m</span>,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => setEditing(row.original)}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alerts · Policies"
        description="Firestore-backed policies evaluated every 5 minutes by AlertEvaluatorCron. Breaches become firings."
      />

      {isError && (
        <ErrorState
          title="Policies unavailable"
          description={humanizeError(error)}
          actionLabel="Retry"
          onAction={() => refetch()}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>All policies</CardTitle>
          <CardDescription>
            Edit a policy to tune its threshold, pause it, or change where it dispatches.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable<NotificationAlertPolicy>
            columns={columns}
            data={data?.policies ?? []}
            loading={isLoading}
            rowKey={(r) => r.policyKey}
            emptyLabel="No policies seeded"
            emptyDescription="Run seedAlertPolicies on admin-back startup."
          />
        </CardContent>
      </Card>

      <EditPolicyDialog policy={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

interface EditPolicyDialogProps {
  policy: NotificationAlertPolicy | null;
  onClose: () => void;
}

function EditPolicyDialog({ policy, onClose }: EditPolicyDialogProps) {
  const mut = useUpdateAlertPolicy();
  const [reason, setReason] = React.useState('');
  const [draft, setDraft] = React.useState<Partial<NotificationAlertPolicy>>({});
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (policy) {
      setDraft({
        displayName: policy.displayName,
        description: policy.description,
        isActive: policy.isActive,
        severity: policy.severity,
        cooldownMinutes: policy.cooldownMinutes,
        runbookUrl: policy.runbookUrl,
        query: { ...policy.query },
        notifyChannels: { ...policy.notifyChannels },
      });
      setReason('');
      setError(null);
    }
  }, [policy]);

  if (!policy) return null;

  const canSubmit = reason.trim().length > 0 && !mut.isPending;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit policy · {policy.policyKey}</DialogTitle>
          <DialogDescription>
            Patch will be validated server-side. Reason is written to audit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Display name</Label>
              <Input
                value={draft.displayName ?? ''}
                onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
              />
            </div>
            <div>
              <Label>Severity</Label>
              <Select
                value={draft.severity}
                onValueChange={(v) => setDraft({ ...draft, severity: v as AlertSeverity })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              rows={2}
              value={draft.description ?? ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label>Active</Label>
              <div className="flex h-10 items-center">
                <Switch
                  checked={Boolean(draft.isActive)}
                  onCheckedChange={(v) => setDraft({ ...draft, isActive: v })}
                />
              </div>
            </div>
            <div>
              <Label>Threshold</Label>
              <Input
                type="number"
                value={draft.query?.threshold ?? 0}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    query: {
                      ...(draft.query ?? policy.query),
                      threshold: Number(e.target.value),
                    },
                  })
                }
              />
            </div>
            <div>
              <Label>Cooldown (min)</Label>
              <Input
                type="number"
                value={draft.cooldownMinutes ?? 0}
                onChange={(e) => setDraft({ ...draft, cooldownMinutes: Number(e.target.value) })}
              />
            </div>
          </div>
          <div>
            <Label>Runbook URL</Label>
            <Input
              value={draft.runbookUrl ?? ''}
              onChange={(e) => setDraft({ ...draft, runbookUrl: e.target.value })}
              placeholder="https://…"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Reason (required)</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Bumping threshold after false positives this weekend"
              maxLength={500}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mut.isPending}>Cancel</Button>
          <Button
            disabled={!canSubmit}
            onClick={async () => {
              try {
                await mut.mutateAsync({ policyKey: policy.policyKey, reason: reason.trim(), patch: draft });
                onClose();
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Save failed');
              }
            }}
          >
            {mut.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
